/* eslint-disable @typescript-eslint/no-explicit-any */
import type { HttpErrorResponse } from '@angular/common/http';

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable, of, Subscription } from 'rxjs';
import {
  catchError,
  expand,
  map,
  takeLast,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { GQLPageInfo } from 'src/app/shared/interfaces/graphql.interface';
import { IUserPermission } from 'src/app/shared/interfaces/permissions.interface';
import {
  AccessLevelsGraphQL,
  IUserGroupNested,
  IUserWithPermissions,
  UserFilters,
} from 'src/app/shared/interfaces/user/user.interface';
import { AlertService } from 'src/app/shared/services/alert.service';
import { ProviderGroupGQLService } from 'src/app/shared/services/provider/provider-group.graphql.service';
import { UserGQLService } from 'src/app/shared/services/users/user.graphql.service';

@Component({
  selector: 'app-user-search-modal',
  templateUrl: './user-search-modal.component.html',
  styleUrls: ['./user-search-modal.component.scss'],
})
export class UserSearchModalComponent implements OnInit, OnDestroy {
  @Input() groups?: IUserGroupNested[];
  @Input() selectedAccountNumbers?: string[];
  @Input() currentUserAccessLevel: AccessLevelsGraphQL;
  @Input() permissions?: IUserPermission[];
  @Input() isPermissionMode: boolean;

  userForm: UntypedFormGroup;
  selectedUser?: IUserDTO;

  usersDTO?: IUserDTO[];
  groupsIDs?: string[];

  isLoading = false;
  isSearchAvilable: boolean;

  private usersPermissionsPageInfo?: GQLPageInfo;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    private activeModal: NgbActiveModal,
    private userGQLService: UserGQLService,
    private providerGroupGQLService: ProviderGroupGQLService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.groupsIDs = this.groups?.map((group) => {
      return group.groupId;
    });

    this.userForm = this.fb.group({
      name: [''],
    });

    this.getUsers({});
  }

  doSearch(isReload = true) {
    const params = this.userForm.value;
    const searchTerm = params.name.split(' ');
    const filter: Record<string, any> = {
      filter: { firstName: searchTerm[0] },
    };

    if (searchTerm[1]) {
      [, filter.lastName] = searchTerm;
    }

    this.getUsers(filter, isReload);
  }

  getUsers(params, isReload = false) {
    this.isLoading = true;
    this.selectedUser = undefined;

    if (isReload) {
      this.usersPermissionsPageInfo = undefined;
      this.usersDTO = [];
    }

    const users$: Observable<IUserDTO[]> = this.isPermissionMode
      ? this.getUsersWithPermissions(params, this.usersDTO)
      : this.getUsersByGroups(params?.filter as UserFilters);

    this.subscriptions.add(
      users$.subscribe({
        next: (users) => {
          this.isLoading = false;
          this.isSearchAvilable = this.isSearchAvilable || users.length > 10;
          this.usersDTO = users;
        },
        error: (error) => {
          this.isLoading = false;
          this.alertService.error(error as HttpErrorResponse | string);
        },
      }),
    );
  }

  private getUsersWithPermissions(
    params?: any,
    previouslyLoadedUsers?: IUserDTO[],
  ): Observable<IUserDTO[]> {
    const usersNumberToLoad = 30;
    const allowedPermissionsIds = (this.permissions ?? []).map((x) => {
      return x.permissionId;
    });

    if (
      this.usersPermissionsPageInfo &&
      !this.usersPermissionsPageInfo.hasNextPage
    ) {
      return of(previouslyLoadedUsers ? previouslyLoadedUsers : []);
    }

    // TODO: the code below should be simplified when getUsersPermissions will filter users by permissions on server
    return this.userGQLService
      .getUsersPermissions({
        allowedPermissionsIds,
        filter: params?.filter,
        after: this.usersPermissionsPageInfo?.endCursor,
      })
      .pipe(
        expand((data) => {
          if (
            data.nodes.length < usersNumberToLoad &&
            data.pageInfo.hasNextPage
          ) {
            return this.userGQLService
              .getUsersPermissions({
                allowedPermissionsIds,
                filter: params?.filter,
                after: data.pageInfo.endCursor,
              })
              .pipe(
                tap((x) => {
                  return (x.nodes = [...data.nodes, ...x.nodes]);
                }),
              );
          }

          return EMPTY;
        }),
        tap((x) => {
          return (this.usersPermissionsPageInfo = x.pageInfo);
        }),
        takeWhile((x) => {
          return x.nodes.length < usersNumberToLoad && x.pageInfo.hasNextPage;
        }, true),
        takeLast(1),
        map((res) => {
          return res.nodes;
        }),
        map((arr) => {
          return arr.map((x) => {
            return this.mapUserWithPermissions(x);
          });
        }),
        map((x) => {
          return previouslyLoadedUsers ? [...previouslyLoadedUsers, ...x] : x;
        }),
      );
  }

  // TODO: the code below should be simplified when getUsersByGroups will filter users by accounts on server
  private getUsersByGroups(_filter?: UserFilters): Observable<IUserDTO[]> {
    if (!this.selectedAccountNumbers) {
      return of([]);
    }

    return this.providerGroupGQLService
      .getProviderGroupsByAccountsUser(this.selectedAccountNumbers)
      .pipe(
        map((res) => {
          const users = res.userGroupsByAccount;
          return users.map((user) => {
            return {
              userName: user.userName,
              firstName: user.firstName,
              lastName: user.lastName,
              groups: user.groupIds.map((x) => {
                return {
                  groupId: x.groupId,
                  name: x.group.name,
                };
              }),
              checked: false,
            } as IUserDTO;
          });
        }),
        catchError(() => {
          return of([]);
        }),
      );
  }

  onCheckboxChange(user: IUserDTO) {
    this.usersDTO = this.usersDTO?.map((node) => {
      return {
        ...node,
        checked:
          node.userName === user.userName && !node.checked ? true : false,
      };
    });

    this.selectedUser =
      user.userName === this.selectedUser?.userName ? undefined : user;
  }

  save() {
    this.activeModal.close(this.selectedUser);
  }

  close() {
    this.activeModal.dismiss();
  }

  onScroll() {
    this.doSearch(false);
  }

  private mapUserWithPermissions(user: IUserWithPermissions): IUserDTO {
    return {
      id: user.id,
      userName: user.userName,
      firstName: user.firstName,
      lastName: user.lastName,
      groups: undefined,
      permissions: user.permissions,
      checked: false,
    };
  }
}

export type IUserSearchModalResult = {
  firstName: string;
  groups?: IUserGroupNested[];
  id: number;
  lastName?: string;
  permissions?: IUserPermission[];
  userName: string;
};

type IUserDTO = {
  checked: boolean;
} & IUserSearchModalResult;
